import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import PaymentModal from '../payment/PaymentModal';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { getAllTarifs } from '../../services/tarifsService';

const PricingPage = ({ onLogin }) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [tarifs, setTarifs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { language } = useLanguage();

    // Get current user from localStorage
    const getUserFromStorage = () => {
        const userUuid = localStorage.getItem('userUuid');
        const userEmail = localStorage.getItem('userEmail');
        const userName = localStorage.getItem('userName');

        if (userUuid) {
            return {
                peopleUuid: userUuid,
                email: userEmail,
                name: userName
            };
        }
        return null;
    };

    // Load tarifs from API
    useEffect(() => {
        const loadTarifs = async () => {
            try {
                const data = await getAllTarifs();
                if (data && data.length > 0) {
                    setTarifs(data);
                }
            } catch (error) {
                console.error('Error loading tarifs:', error);
            } finally {
                setLoading(false);
            }
        };
        loadTarifs();
    }, []);

    const handlePlanSelect = (plan) => {
        const user = getUserFromStorage();
        const code = (plan.code ?? plan.CODE ?? '').toUpperCase();
        const isFree = code === 'FREE';

        if (!user) {
            navigate('/register', { state: { selectedPlan: code.toLowerCase() } });
            return;
        }

        if (!isFree) {
            setSelectedPlan(code);
            setShowPaymentModal(true);
        } else {
            navigate('/');
        }
    };

    // Determine which tarif is "popular" (the one with highest sortOrder that's not FULL)
    const getIsPopular = (tarif) => {
        const code = (tarif.code ?? tarif.CODE ?? '').toUpperCase();
        return code === 'PRO';
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Header onLogin={onLogin} />

            <main className="flex-1">
                {/* Hero Section */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        {t.pricing?.title || 'Choisissez votre'} <span className="text-cabaza-purple">{t.pricing?.titleHighlight || 'formule'}</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        {t.pricing?.subtitle || 'Des tarifs simples et transparents. Pas de frais cachés.'}
                    </p>
                </div>

                {/* Billing Period Toggle */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    <div className="bg-gray-100 p-1 rounded-xl flex items-center gap-1">
                        <button
                            onClick={() => setBillingPeriod('monthly')}
                            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                billingPeriod === 'monthly'
                                    ? 'bg-white text-cabaza-purple shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {t.pricing?.monthly || 'Mensuel'}
                        </button>
                        <button
                            onClick={() => setBillingPeriod('yearly')}
                            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                billingPeriod === 'yearly'
                                    ? 'bg-white text-cabaza-purple shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {t.pricing?.yearly || 'Annuel'}
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                -17%
                            </span>
                        </button>
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 text-cabaza-purple animate-spin" />
                        </div>
                    ) : (
                        <div className={`grid grid-cols-1 gap-8 ${
                            tarifs.length === 4 ? 'md:grid-cols-4' :
                            tarifs.length === 3 ? 'md:grid-cols-3' :
                            'md:grid-cols-2'
                        }`}>
                            {tarifs.map((tarif) => {
                                const code = (tarif.code ?? tarif.CODE ?? '').toUpperCase();
                                const isFree = code === 'FREE';
                                const isPopular = getIsPopular(tarif);
                                const monthlyPrice = parseFloat(tarif.monthlyprice ?? tarif.monthlyPrice ?? tarif.MONTHLYPRICE ?? 0);
                                const yearlyPrice = parseFloat(tarif.yearlyprice ?? tarif.yearlyPrice ?? tarif.YEARLYPRICE ?? 0);
                                const displayPrice = billingPeriod === 'yearly' ? yearlyPrice : monthlyPrice;
                                const periodLabel = billingPeriod === 'yearly' ? (t.pricing?.perYear || '/an') : (t.pricing?.perMonth || '/mois');
                                const tarifName = tarif[`name_${language}`] || tarif.name_fr || tarif.NAME_FR || tarif.name || code;
                                const tarifDescription = tarif[`description_${language}`] || tarif.description_fr || tarif.DESCRIPTION_FR || '';

                                return (
                                    <div
                                        key={code}
                                        className={`relative rounded-2xl p-8 ${
                                            isPopular
                                                ? 'bg-linear-to-br from-cabaza-purple to-cabaza-pink text-white shadow-2xl scale-105 z-10'
                                                : 'bg-white border-2 border-gray-100 shadow-lg hover:shadow-xl hover:border-cabaza-purple/20'
                                        } transition-all duration-300`}
                                    >
                                        {/* Popular Badge */}
                                        {isPopular && (
                                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                                <span className="inline-flex items-center gap-1 bg-white text-cabaza-purple px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                                                    <Sparkles className="w-4 h-4" />
                                                    {t.pricing?.popular || 'Populaire'}
                                                </span>
                                            </div>
                                        )}

                                        {/* Plan Name */}
                                        <h3 className={`text-2xl font-bold mb-2 ${isPopular ? 'text-white' : 'text-gray-900'}`}>
                                            {tarifName}
                                        </h3>
                                        <p className={`text-sm mb-6 ${isPopular ? 'text-white/80' : 'text-gray-500'}`}>
                                            {tarifDescription}
                                        </p>

                                        {/* Price */}
                                        <div className="mb-8">
                                            <span className={`text-5xl font-bold ${isPopular ? 'text-white' : 'text-gray-900'}`}>
                                                {displayPrice}€
                                            </span>
                                            <span className={`text-lg ${isPopular ? 'text-white/80' : 'text-gray-500'}`}>
                                                {periodLabel}
                                            </span>
                                            {billingPeriod === 'yearly' && monthlyPrice > 0 && (
                                                <div className={`mt-1 text-xs ${isPopular ? 'text-white/70' : 'text-green-600'}`}>
                                                    {t.subscription?.savePerYear || 'Économisez'} {Math.round(monthlyPrice * 12 - yearlyPrice)}€/{t.subscription?.perYear || 'an'}
                                                </div>
                                            )}
                                        </div>

                                        {/* CTA Button */}
                                        {isFree ? (
                                            <Link
                                                to="/register"
                                                className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all duration-200 bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white hover:shadow-lg hover:scale-105`}
                                            >
                                                {t.pricing?.startFree || 'Commencer gratuitement'}
                                            </Link>
                                        ) : (
                                            <button
                                                onClick={() => handlePlanSelect(tarif)}
                                                className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all duration-200 ${
                                                    isPopular
                                                        ? 'bg-white text-cabaza-purple hover:bg-gray-100 hover:shadow-lg'
                                                        : 'bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white hover:shadow-lg hover:scale-105'
                                                }`}
                                            >
                                                {t.pricing?.choosePlan || 'Choisir'} {tarifName}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Preferential rates note */}
                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500 italic">
                            {t.pricing?.preferentialRates || 'Tarifs préférentiels disponibles pour les étudiants et demandeurs d\'emploi.'}
                        </p>
                    </div>
                </div>

                {/* Payment Methods Section */}
                <div className="py-12 bg-gray-50">
                    <div className="max-w-3xl mx-auto px-4 text-center">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t.pricing?.paymentMethods || 'Moyens de paiement acceptés'}</h3>
                        <div className="flex items-center justify-center gap-6 flex-wrap">
                            <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Credit Card" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/paypal.svg" alt="PayPal" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/applepay.svg" alt="Apple Pay" className="h-8" />
                        </div>
                        <p className="text-sm text-gray-500 mt-4">{t.pricing?.securedBy || 'Paiements sécurisés par Mollie'}</p>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                        {t.pricing?.faq?.title || 'Questions fréquentes'}
                    </h2>
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q1 || 'Puis-je changer de formule à tout moment ?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a1 || 'Oui, vous pouvez passer à une formule supérieure ou inférieure à tout moment. Le changement prend effet immédiatement.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q2 || 'Y a-t-il un engagement ?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a2 || 'Non, toutes nos formules sont sans engagement. Vous pouvez annuler à tout moment.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q3 || 'Les tarifs sont-ils les mêmes pour tous les profils ?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a3 || 'Des réductions sont disponibles selon votre profil (étudiant, demandeur d\'emploi). Les tarifs adaptés vous seront proposés lors de l\'inscription.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q4 || 'Comment fonctionne le paiement ?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a4 || 'Le paiement est sécurisé via Mollie. Vous pouvez payer par Bancontact, carte bancaire, PayPal ou iDEAL.'}
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />

            {/* Payment Modal */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                planType={selectedPlan}
                user={getUserFromStorage()}
            />
        </div>
    );
};

export default PricingPage;
